import { Fragment, useMemo } from "react"

// Hooks
import { useCurrentFinanceProject } from "../../hooks/useCurrentFinanceProject"

// Icons
import { FiDownload } from "@/lib/icons"

// Translations
import { useTrans } from "@/i18n"

// UI
import { cn } from "@/lib/utils"
import { Heading } from "@/components/Typography"
import { Card } from "@/components/Card"
import {
	Table,
	TableBody,
	TableDataCell,
	TableHead,
	TableHeading,
	TableRowCell,
} from "@/components/table-controls/TableItems"
import {
	getCoreRowModel,
	useReactTable,
	createColumnHelper,
	flexRender,
} from "@/lib/table"

// REST queries
import { useApiV1ComparisonProjectEndexTariffsList } from "@/api/rest/generated/api/api"

// Types
import { EndexTariff } from "@/api/rest/generated/@types"

const columnHelper = createColumnHelper<EndexTariff>()

export function SolarInterestTariffDownloads() {
	const currentProject = useCurrentFinanceProject().data.project
	const projectId = Number(currentProject?.id) || 1 // TODO: Pick a default

	// Translate
	const t = useTrans("finance")

	// Rest Query
	const { data } = useApiV1ComparisonProjectEndexTariffsList(projectId)

	const columns = useMemo(
		() => [
			columnHelper.accessor((data) => data, {
				id: "period",
				header: () => (
					<TableHeading variant="static">
						{t(
							"finance:solar_interest_tariff.block.solar-tariffs.table.headers.period",
						)}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell multiline>
						{`${info.getValue().start} - ${info.getValue().end}`}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("fixed_surcharge", {
				header: () => (
					<TableHeading variant="static">
						{t(
							"finance:solar_interest_tariff.block.solar-tariffs.table.headers.fixed_surcharge",
						)}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>
						{t(
							"finance:solar_interest_tariff.graph.tooltip.average_tariffs.value",
							{
								price: info.getValue(),
							},
						)}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("average_endex_tariff", {
				header: () => (
					<TableHeading variant="static">
						{t(
							"finance:solar_interest_tariff.block.solar-tariffs.table.headers.average_endex_tariff",
						)}
						<div className="hidden">
							{t(
								"finance:solar_interest_tariff.block.solar-tariffs.table.headers.average_endex_tariff.tooltip",
							)}
						</div>
					</TableHeading>
				),
				cell: ({ row }) => (
					<TableDataCell>
						{t(
							"finance:solar_interest_tariff.graph.tooltip.average_tariffs.value",
							{
								price: parseFloat(
									row.original.average_endex_tariff,
								).toFixed(2),
							},
						)}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("endex_total", {
				header: () => (
					<TableHeading variant="static">
						{t(
							"finance:solar_interest_tariff.block.solar-tariffs.table.headers.electricity_price",
						)}
					</TableHeading>
				),
				cell: ({ row }) => (
					<TableDataCell>
						{t(
							"finance:solar_interest_tariff.graph.tooltip.average_tariffs.value",
							{
								price: parseFloat(
									row.original.endex_total,
								).toFixed(2),
							},
						)}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("export_link", {
				header: () => (
					<TableHeading variant="static" className="w-20">
						{t(
							"finance:solar_interest_tariff.block.solar-tariffs.table.headers.export_link",
						)}
					</TableHeading>
				),
				cell: ({ row }) => (
					<TableDataCell>
						<a
							href={row.original.export_link}
							className={cn(
								"text-gray-500 hover:text-gray-700",
								"group px-4",
								"text-sm font-medium whitespace-nowrap",
							)}
						>
							<FiDownload />
						</a>
					</TableDataCell>
				),
			}),
		],
		[t],
	)
	const tableData = useMemo(() => data ?? [], [data])
	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
	})

	return (
		<Card className="mt-5 h-full">
			<Heading as="h2" styleAs="h5" className="mb-4 text-gray-600">
				{t(
					"dashboard:stats.project_stats.table.solar_interest_periods.title",
				)}
			</Heading>
			<div className="-mx-4 overflow-x-auto sm:mx-0">
				<Table>
					<TableHead>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRowCell key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<Fragment key={header.id}>
											{flexRender(
												header.column.columnDef.header,
												header.getContext(),
											)}
										</Fragment>
									)
								})}
							</TableRowCell>
						))}
					</TableHead>
					<TableBody data-testid="tablebody">
						{table.getRowModel().rows.map((row, index) => {
							return (
								<TableRowCell
									key={row.id}
									isOdd={index % 2 === 0}
									data-testid={`tablerow-${row.id}`}
								>
									{row.getAllCells().map((cell) => {
										return (
											<Fragment key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</Fragment>
										)
									})}
								</TableRowCell>
							)
						})}
					</TableBody>
				</Table>
			</div>
		</Card>
	)
}
